import * as React from "react";
import Grid from "@mui/material/Grid";
import { useMutation, useQuery } from "react-query";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { LltsLanguage, MachineTranslationService, UtilsService, PostEditTextResponse } from "gen/clients/llts";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { SnackbarAlert } from "components/SnackbarAlert/SnackbarAlert";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { CopyToClipboardButton } from "components/CopyToClipboardButton/CopyToClipboardButton";
import { SelectOption } from "@mui/base";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import Typography from "@mui/material/Typography";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link } from "@mui/material";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { LinguisticQualityReview } from "../components/MtJobRequestDetailsPage/components/LinguisticQualityReview";

enum FieldNames {
  sourceLanguage = "sourceLanguage",
  targetLanguage = "targetLanguage",
  sourceText = "sourceText"
}

interface FormValues {
  [FieldNames.sourceLanguage]?: SelectOption<string>;
  [FieldNames.targetLanguage]?: SelectOption<string>;
  [FieldNames.sourceText]: string;
}

const AUTODETECT_LANGUAGE = "AUTODETECT_LANGUAGE";
const DEFAULT_POST_EDIT_SOURCE_LANGUAGE_ID = "enu";

const TextTranslationTab: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [sourceTextLength, setSourceTextLength] = React.useState<number>(0);
  const [translatedText, setTranslatedText] = React.useState<string>("0");
  const [isInputChanged, setInputChanged] = React.useState(false);
  const [postEditedResult, setPostEditedResult] = React.useState<PostEditTextResponse | null>(null);
  const [activeTab, setActiveTab] = React.useState<"translation" | "postEdited">("translation");
  const [showPostEditSuccess, setShowPostEditSuccess] = React.useState(false);
  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });
  const {
    data: lltsLanguages,
    isLoading: areLltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["listIntentoLltsLanguages"], {
    queryFn: () =>
      UtilsService.listLltsLanguages({
        azureIdExists: true
      })
  });
  const lltsLanguageById: Record<string, LltsLanguage> = React.useMemo(
    () =>
      (lltsLanguages || []).reduce(
        (prevValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          prevValue[currentValue.id] = currentValue;
          return prevValue;
        },
        {} as Record<string, LltsLanguage>
      ),
    [lltsLanguages]
  );
  const languageOptions = React.useMemo(
    () =>
      mtSettings?.supportedLltsLanguages
        ?.filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId].isDisabled)
        .map(langId => ({
          label: lltsLanguageById[langId]?.name || langId,
          value: langId
        }))
        .sort(SELECT_OPTION_COMPARATOR) || [],
    [mtSettings, lltsLanguageById]
  );

  const sourceLanguageOptions: SelectOption<string>[] = React.useMemo(
    () => [{ label: t("machineTranslation.autoDetect"), value: AUTODETECT_LANGUAGE }, ...languageOptions],
    [languageOptions, t]
  );

  const {
    mutateAsync: translate,
    data: translation,
    isLoading: isTranslating,
    error: translateError
  } = useMutation(MachineTranslationService.translateText, {
    onSuccess: () => {
      setInputChanged(false);
      setPostEditedResult(null);
      setActiveTab("translation");
      setShowPostEditSuccess(false);
    }
  });

  const {
    mutateAsync: postEditText,
    isLoading: isPostEditing,
    error: postEditError
  } = useMutation(MachineTranslationService.postEditText);

  React.useEffect(() => {
    setTranslatedText(translation?.translatedText || "");
  }, [translation?.translatedText]);

  const languageDetectedLabel = React.useMemo(() => {
    if (translation?.detectedLanguage && !isInputChanged) {
      return `${t("machineTranslation.languageDetected", { language: translation.detectedLanguage })}`;
    }
    return undefined;
  }, [t, translation?.detectedLanguage, isInputChanged]);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const sourceLanguage = values[FieldNames.sourceLanguage];
      const targetLanguage = values[FieldNames.targetLanguage];
      const sourceText = values[FieldNames.sourceText];
      if (!targetLanguage || !sourceText.trim() || !isInputChanged) {
        return;
      }
      await translate({
        requestBody: {
          text: sourceText,
          sourceLanguageCode:
            !sourceLanguage || sourceLanguage.value === AUTODETECT_LANGUAGE ? undefined : sourceLanguage.value,
          targetLanguageCode: targetLanguage.value
        }
      });
    },
    [translate, isInputChanged]
  );

  const onSourceTextChange = React.useCallback(
    (text: string) => {
      setSourceTextLength(text.length);
      setInputChanged(true);
      // Clear post-edited result when source text changes
      setPostEditedResult(null);
      setActiveTab("translation");
      setShowPostEditSuccess(false);
    },
    [setSourceTextLength]
  );

  const findLanguageIdByName = React.useCallback(
    (detectedLanguageName: string): string => {
      if (!lltsLanguages || !detectedLanguageName) {
        return DEFAULT_POST_EDIT_SOURCE_LANGUAGE_ID;
      }

      // Try to find a language that partially matches the detected language name (case-insensitive)
      const matchingLanguage = lltsLanguages.find(
        lang =>
          lang.name.toLowerCase().includes(detectedLanguageName.toLowerCase()) ||
          detectedLanguageName.toLowerCase().includes(lang.name.toLowerCase())
      );

      return matchingLanguage?.id || DEFAULT_POST_EDIT_SOURCE_LANGUAGE_ID;
    },
    [lltsLanguages]
  );

  const onPostEditText = React.useCallback(
    async (values: FormValues) => {
      const sourceLanguage = values[FieldNames.sourceLanguage];
      const targetLanguage = values[FieldNames.targetLanguage];
      const sourceText = values[FieldNames.sourceText];

      if (!targetLanguage || !sourceText.trim() || !translatedText || !translation) {
        return;
      }

      try {
        let sourceLanguageId: string;
        if (!sourceLanguage || sourceLanguage.value === AUTODETECT_LANGUAGE) {
          // Use the detected language to find matching language ID
          sourceLanguageId = translation.detectedLanguage
            ? findLanguageIdByName(translation.detectedLanguage)
            : DEFAULT_POST_EDIT_SOURCE_LANGUAGE_ID;
        } else {
          sourceLanguageId = sourceLanguage.value;
        }

        const result = await postEditText({
          requestBody: {
            sourceLanguageId,
            sourceText: sourceText,
            targetLanguageId: targetLanguage.value,
            targetText: translatedText
          }
        });
        setPostEditedResult(result);
        setActiveTab("postEdited");
        setShowPostEditSuccess(true);
      } catch (error) {
        console.error("Post-editing failed:", error);
      }
    },
    [postEditText, translatedText, translation, findLanguageIdByName]
  );

  const onSourceLanguageChanged = React.useCallback(() => {
    setInputChanged(true);
  }, [setInputChanged]);

  const sourceTextHelper = React.useMemo(() => {
    if (!mtSettings?.maxCharactersPerRequest) {
      return undefined;
    }
    return `${sourceTextLength.toLocaleString()}/${mtSettings.maxCharactersPerRequest.toLocaleString()}`;
  }, [sourceTextLength, mtSettings?.maxCharactersPerRequest]);

  const isLoading = areMtSettingsLoading || areLltsLanguagesLoading;
  const error = mtSettingsError || lltsLanguagesError;

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  return (
    <Box>
      <Formik
        initialValues={{
          [FieldNames.sourceText]: "",
          [FieldNames.sourceLanguage]: { label: t("machineTranslation.autoDetect"), value: AUTODETECT_LANGUAGE },
          [FieldNames.targetLanguage]: undefined
        }}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => {
          const isSourceLanguageRtl =
            values[FieldNames.sourceLanguage] &&
            values[FieldNames.sourceLanguage].value !== AUTODETECT_LANGUAGE &&
            lltsLanguageById[values[FieldNames.sourceLanguage].value]?.isRightToLeft;

          const isTargetLanguageRtl =
            values[FieldNames.targetLanguage] &&
            lltsLanguageById[values[FieldNames.targetLanguage].value]?.isRightToLeft;

          return (
            <Form noValidate={true} autoComplete="off">
              <FormFieldObserver fieldName={FieldNames.sourceText} onChange={onSourceTextChange} />
              <FormFieldObserver fieldName={FieldNames.sourceLanguage} onChange={onSourceLanguageChanged} />
              <FormFieldObserver fieldName={FieldNames.targetLanguage} onChange={() => setInputChanged(true)} />
              <Grid container={true} spacing={isSmallScreen ? 2 : 1} mt={2}>
                <Grid item={true} xs={12} md={5}>
                  <SelectOneAutocompleteField
                    name={FieldNames.sourceLanguage}
                    label={t("machineTranslation.sourceLanguage")}
                    size="small"
                    options={sourceLanguageOptions}
                    variant="outlined"
                    required={true}
                  />
                </Grid>
                <Grid item={true} xs={12} md={2} textAlign="center" order={isSmallScreen ? 6 : 2}>
                  <Stack>
                    <LoadingButton isLoading={isTranslating}>{t("machineTranslation.translate")}</LoadingButton>
                    <Link
                      component="button"
                      type="button"
                      sx={{ my: 1 }}
                      onClick={() => {
                        setFieldValue(FieldNames.sourceText, "");
                        setTranslatedText("");
                      }}
                    >
                      {t("machineTranslation.clear")}
                    </Link>
                  </Stack>
                </Grid>
                <Grid item={true} xs={12} md={5} order={isSmallScreen ? 2 : 3}>
                  <SelectOneAutocompleteField
                    name={FieldNames.targetLanguage}
                    label={t("machineTranslation.targetLanguage")}
                    size="small"
                    options={languageOptions}
                    variant="outlined"
                    required={true}
                  />
                </Grid>
                <Grid item={true} xs={12} md={6} order={isSmallScreen ? 3 : 4}>
                  <Stack>
                    {/* Source Text Field with Header */}
                    <Box
                      sx={{
                        height: isSmallScreen ? "auto" : "480px", // Match the translation field height exactly
                        display: "flex",
                        flexDirection: "column",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        overflow: "hidden"
                      }}
                    >
                      {/* Source Text Header */}
                      <Box
                        sx={{
                          px: 2,
                          backgroundColor: theme.palette.grey[50],
                          minHeight: 40,
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {t("machineTranslation.sourceText")}
                        </Typography>
                      </Box>

                      {/* Source Text Input */}
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <TextInputField
                          name={FieldNames.sourceText}
                          multiline={true}
                          rows={isSmallScreen ? 10 : 20}
                          variant="outlined"
                          placeholder={t("machineTranslation.sourceTextPrompt")}
                          maxLength={mtSettings?.maxCharactersPerRequest}
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                              height: "100%",
                              alignItems: "flex-start",
                              "& fieldset": {
                                border: "none" // Remove border since we have the container border
                              },
                              "&:hover fieldset": {
                                border: "none"
                              },
                              "&.Mui-focused fieldset": {
                                border: "none"
                              }
                            },
                            "& .MuiInputBase-inputMultiline": {
                              height: "100% !important",
                              overflow: "auto !important",
                              direction: isSourceLanguageRtl ? "rtl" : "ltr",
                              textAlign: isSourceLanguageRtl ? "right" : "left"
                            },
                            "& .MuiInputLabel-root": {
                              display: "none" // Hide the label completely
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Character Count and Language Detection */}
                    <Stack direction="row" spacing={2} ml={2} mr={2} mt={1}>
                      <Typography variant="body2" color="textSecondary" flexGrow={1}>
                        {sourceTextHelper}
                      </Typography>
                      {values[FieldNames.sourceLanguage]?.value === AUTODETECT_LANGUAGE && (
                        <Typography variant="body2" color="textSecondary" align="right" justifyContent="flex-end">
                          {languageDetectedLabel}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Grid>
                {(!isSmallScreen || translatedText) && (
                  <Grid item={true} xs={12} md={6} order={isSmallScreen ? 5 : 6}>
                    <Stack>
                      {/* Translation TextField with integrated linguistic quality review */}
                      <Box
                        sx={{
                          height: isSmallScreen ? "auto" : "480px", // Match the source text field height exactly
                          display: "flex",
                          flexDirection: "column",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: "hidden"
                        }}
                      >
                        {/* Header - Tab Interface or Simple Text */}
                        <Box sx={{ backgroundColor: theme.palette.grey[50] }}>
                          {postEditedResult ? (
                            // Show tabs when there are multiple tabs
                            <Tabs
                              value={activeTab}
                              onChange={(_, newValue) => setActiveTab(newValue)}
                              variant="standard"
                              sx={{
                                minHeight: 40,
                                "& .MuiTab-root": {
                                  minHeight: 40,
                                  fontSize: "0.875rem",
                                  fontWeight: 600,
                                  textTransform: "none"
                                }
                              }}
                            >
                              <Tab label={t("machineTranslation.translation")} value="translation" />
                              <Tab label={t("machineTranslation.aiPostEditedText")} value="postEdited" />
                            </Tabs>
                          ) : (
                            // Show simple text header when there's only one tab
                            <Box
                              sx={{
                                px: 2,
                                backgroundColor: theme.palette.grey[50],
                                minHeight: 40,
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                {t("machineTranslation.translation")}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Tab Content */}
                        <Box sx={{ flex: 1, p: 2, overflow: "auto", minHeight: 0 }}>
                          {activeTab === "translation" && (
                            <Typography
                              component="div"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: translatedText ? theme.palette.grey["700"] : theme.palette.text.disabled,
                                minHeight: "100px",
                                direction: isTargetLanguageRtl ? "rtl" : "ltr",
                                textAlign: isTargetLanguageRtl ? "right" : "left"
                              }}
                            >
                              {translatedText || ""}
                            </Typography>
                          )}
                          {activeTab === "postEdited" && postEditedResult && (
                            <Typography
                              component="div"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: theme.palette.grey["700"],
                                minHeight: "100px",
                                direction: isTargetLanguageRtl ? "rtl" : "ltr",
                                textAlign: isTargetLanguageRtl ? "right" : "left"
                              }}
                            >
                              {postEditedResult.correctedText}
                            </Typography>
                          )}
                        </Box>

                        {/* AI Linguistic Quality Review Section - only show for translation tab */}
                        {activeTab === "translation" &&
                          mtSettings?.enableAiLinguisticQualityReview &&
                          translation?.aiLinguisticQualityReviewResult &&
                          values[FieldNames.targetLanguage] && (
                            <Box sx={{ px: 2, pb: 2 }}>
                              <LinguisticQualityReview
                                reviewResult={translation.aiLinguisticQualityReviewResult}
                                languageName={values[FieldNames.targetLanguage].label as string}
                              />
                            </Box>
                          )}
                      </Box>

                      {/* Action Buttons - positioned below the Translation box */}
                      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                        {mtSettings?.enableAiPostEditing && (
                          <LoadingButton
                            variant={
                              (translation?.aiLinguisticQualityReviewResult?.score || 0) < 5 ? "contained" : "outlined"
                            }
                            size="small"
                            isLoading={isPostEditing}
                            disabled={!translatedText || isPostEditing || !!postEditedResult || isInputChanged}
                            onClick={() => onPostEditText(values)}
                          >
                            {t("machineTranslation.postEditWithAi")}
                          </LoadingButton>
                        )}
                        <CopyToClipboardButton
                          textToCopy={
                            activeTab === "postEdited" && postEditedResult
                              ? postEditedResult.correctedText
                              : translatedText
                          }
                          disabled={activeTab === "postEdited" ? !postEditedResult : !translatedText}
                        >
                          {t("machineTranslation.copy")}
                        </CopyToClipboardButton>
                      </Stack>
                    </Stack>
                  </Grid>
                )}
                <Grid item={true} xs={12} order={7}>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.grey["700"] }}
                    dangerouslySetInnerHTML={{ __html: t("machineTranslation.disclaimer") }}
                  />
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      {translateError && <SnackbarApiError error={translateError} />}
      {postEditError && <SnackbarApiError error={postEditError} />}
      {showPostEditSuccess && (
        <SnackbarAlert
          message={t("machineTranslation.postEditingSuccess")}
          severity="success"
          vertical="top"
          horizontal="center"
          autoHideDuration={6000}
        />
      )}
    </Box>
  );
};

export { TextTranslationTab };
